import React from 'react';
import { useTheme } from '@mui/material';
import BaseChart from './BaseChart';

/**
 * ProviderDistributionChart Component
 *
 * Displays provider distribution using a pie chart with:
 * - OpenAI vs Private distribution
 * - Legend at top-left (same as cost breakdown)
 * - Interactive tooltips
 * - Responsive design
 */
const ProviderDistributionChart = ({ data, period, loading = false }) => {
  const theme = useTheme();

  if (
    loading ||
    !data ||
    !data.provider_distribution ||
    data.provider_distribution.length === 0
  ) {
    return (
      <BaseChart
        title={`Provider Distribution (${period || 'N/A'})`}
        subtitle="OpenAI vs Private provider usage"
        type="pie"
        series={[]}
        height={350}
        loading={loading}
      />
    );
  }

  // Use theme colors for chart
  const primaryColor = theme.palette.primary.main;
  const successColor = theme.palette.success.main;

  const chartOptions = {
    labels: data.provider_distribution.map(provider => provider.provider),
    colors: [primaryColor, successColor],
    chart: {
      type: 'pie',
      height: 350,
      offsetX: 0,
      offsetY: 0,
      sparkline: {
        enabled: false,
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
      markers: {
        width: 12,
        height: 12,
        radius: 2,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 8,
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12px',
              fontWeight: 600,
              color: '#333',
            },
            value: {
              show: true,
              fontSize: '14px',
              fontWeight: 'bold',
              color: primaryColor,
              formatter: function (val) {
                return val ? val.toLocaleString() : '0';
              },
            },
            total: {
              show: true,
              showAlways: true,
              label: 'Total',
              fontSize: '12px',
              fontWeight: 600,
              color: '#333',
              formatter: function (w) {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return total.toLocaleString();
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        return (
          opts.w.config.labels[opts.seriesIndex] + ': ' + val.toFixed(1) + '%'
        );
      },
      style: {
        fontSize: '11px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: false,
      intersect: true,
      fillSeriesColor: false,
      style: {
        fontSize: '12px',
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const provider = data.provider_distribution[seriesIndex];
        if (!provider) {
          return '';
        }

        return `
          <div style="
            padding: 8px 12px; 
            background: white; 
            border: 1px solid #e0e0e0; 
            border-radius: 6px; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.15); 
            font-family: 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif;
            line-height: 1.4;
          ">
            <div style="
              font-weight: 600; 
              margin-bottom: 4px; 
              color: ${primaryColor}; 
              font-size: 13px;
            ">${provider.provider}</div>
            <div style="margin-bottom: 2px; font-size: 12px;">
              Executions: <strong>${provider.executions.toLocaleString()}</strong>
            </div>
            <div style="margin-bottom: 2px; font-size: 12px;">
              Percentage: <strong>${provider.percentage}%</strong>
            </div>
          </div>
        `;
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 300,
          },
          legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '11px',
            itemMargin: {
              horizontal: 10,
              vertical: 8,
            },
          },
          plotOptions: {
            pie: {
              donut: {
                size: '55%',
              },
            },
          },
        },
      },
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 280,
          },
          legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '10px',
            itemMargin: {
              horizontal: 10,
              vertical: 8,
            },
          },
          plotOptions: {
            pie: {
              donut: {
                size: '50%',
              },
            },
          },
        },
      },
    ],
  };

  const series = data.provider_distribution.map(
    provider => provider.executions
  );

  return (
    <BaseChart
      title={`Provider Distribution (${period || 'N/A'})`}
      subtitle="OpenAI vs Private provider usage"
      type="pie"
      series={series}
      options={chartOptions}
      height={350}
      loading={loading}
    />
  );
};

export default ProviderDistributionChart;
